import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './FilingPurchase.css'; // We'll reuse the filing purchase styles

const FilingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Get payment data and form details from location state
  const paymentData = location.state?.paymentData;
  const formDetails = location.state?.formDetails;

  useEffect(() => {
    // If no payment data or form details, redirect to home
    if (!paymentData || !formDetails) {
      navigate('/');
      return;
    }

    // If user not logged in, redirect to signin
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    // Track filing purchase conversion
    if (typeof window.gtag !== 'undefined') {
      // Get UTM parameters if they exist
      const urlParams = new URLSearchParams(window.location.search);
      const utmData = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content')
      };

      // Track filing purchase as conversion
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17465397731/POpFCI6RsYUbEOOjk4hB',
        'value': paymentData.amount || 1.0,
        'currency': 'USD',
        'transaction_id': paymentData.id || Date.now().toString(),
        'custom_parameters': {
          'form_type': getFormTypeLabel(),
          'source': utmData.utm_source || 'direct',
          'medium': utmData.utm_medium || 'none',
          'campaign': utmData.utm_campaign || 'none',
          'filing_purchase_completed': true
        }
      });

      // Track as purchase event for GA4
      window.gtag('event', 'purchase', {
        transaction_id: paymentData.id || Date.now().toString(),
        value: paymentData.amount || 1.0,
        currency: 'USD',
        items: [{
          item_id: formDetails.id || 'filing',
          item_name: formDetails.form_name || 'Tax Filing Service',
          category: 'tax_filings',
          quantity: 1,
          price: paymentData.amount || 1.0
        }]
      });
    }

  }, [paymentData, formDetails, currentUser, navigate]);

  // Get form type for display
  const getFormTypeLabel = () => {
    if (!formDetails) return 'filing';
    // We can expand this based on form categories/types
    return formDetails.form_name || 'filing';
  };

  // Handle if navigated directly to this page without payment data
  if (!paymentData || !formDetails) {
    return null; // useEffect will handle redirect
  }

  return (
    <div className="filing-purchase-page">
      <div className="container">
        <div className="filing-purchase-container">
          <div className="confirmation-header">
            <div className="confirmation-icon">
              <span>✓</span>
            </div>
            <h1>Filing Purchase Confirmed</h1>
          </div>

          <div className="confirmation-message">
            <p>Thank you for purchasing our {getFormTypeLabel()} filing service.</p>
            <p>We've sent a confirmation email to <strong>{currentUser.email}</strong> with your receipt and next steps.</p>
          </div>

          <div className="filing-details">
            <h2>Purchase Details</h2>
            <div className="filing-detail-item">
              <span>Form:</span>
              <span>{formDetails.form_name}</span>
            </div>
            <div className="filing-detail-item">
              <span>Amount Paid:</span>
              <span>${(paymentData.amount || 0).toFixed(2)}</span>
            </div>
            <div className="filing-detail-item">
              <span>Order ID:</span>
              <span>{paymentData.id || 'N/A'}</span>
            </div>
            <div className="filing-detail-item">
              <span>Purchase Date:</span>
              <span>{new Date(paymentData.created_at || Date.now()).toLocaleDateString()}</span>
            </div>
            <div className="filing-detail-item">
              <span>Status:</span>
              <span className={`status-${paymentData.status?.toLowerCase() || 'pending'}`}>
                {paymentData.status || 'Pending'}
              </span>
            </div>
          </div>
          
          <div className="next-steps">
            <h2>What's Next?</h2>
            <ol>
              <li>
                <strong>Check your email</strong> - You'll receive a detailed email with next steps and any additional information we need to proceed with your {getFormTypeLabel()}.
              </li>
              <li>
                <strong>Complete filing questionnaire</strong> - We'll be sending you a questionnaire to collect necessary information for your filing.
              </li>
              <li>
                <strong>Document preparation</strong> - Our team will prepare your filing documents and initiate the submission process.
              </li>
              <li>
                <strong>Track your filing</strong> - You can check the status of your filing in your account dashboard.
              </li>
            </ol>
          </div>

          <div className="confirmation-actions">
            <Link to="/profile" className="button primary-button">
              Go to My Account
            </Link>
            <Link to="/filings" className="button secondary-button">
              View All Filings
            </Link>
          </div>

          <div className="contact-support">
            <p>Questions about your filing? Contact our support team at <a href="mailto:support@rkubeservices.com">support@rkubeservices.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilingConfirmation; 